// This helper is used to parse flattened query strings into a nested object.
// Query-string keys are user-controlled, and this util
// avoids looped dynamic writes on plain objects (prototype-pollution sink pattern).

const createSafeObject = () => Object.create(null) as Record<string, unknown>;
const UNSAFE_OBJECT_KEYS = new Set(["__proto__", "prototype", "constructor"]);
type NestedMap = Map<string, NestedMap | unknown>;

const hasUnsafeKey = (parts: string[]): boolean =>
  parts.some((part) => UNSAFE_OBJECT_KEYS.has(part));

const mapToSafeObject = (node: NestedMap): Record<string, unknown> => {
  const result = createSafeObject();

  for (const [key, value] of node.entries()) {
    Object.defineProperty(result, key, {
      value: value instanceof Map ? mapToSafeObject(value) : value,
      enumerable: true,
      writable: true,
      configurable: true,
    });
  }

  return result;
};

// Unflattens a flat object into a nested object.
// Example usage: { "groups.123": ["A"] } becomes
// { groups: { "123": ["A"] } }.
export default function unflatten(
  obj: Record<string, any>,
  delimiter: string
): Record<string, any> {
  const root: NestedMap = new Map();

  for (const [key, value] of Object.entries(obj)) {
    const parts = key.split(delimiter);

    if (hasUnsafeKey(parts)) {
      continue;
    }

    let currentNode = root;

    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      const existingNode = currentNode.get(part);

      if (existingNode instanceof Map) {
        currentNode = existingNode;
        continue;
      }

      const nextNode: NestedMap = new Map();
      currentNode.set(part, nextNode);
      currentNode = nextNode;
    }

    currentNode.set(parts[parts.length - 1], value);
  }

  return mapToSafeObject(root);
}
